// 回收站服务 - 支持软删除和恢复功能
class RecycleBinService {
  constructor() {
    this.recycleBinKey = 'altverse-recycle-bin'
    this.maxRetentionDays = 30 // 最大保留30天
    this.autoCleanupInterval = 24 * 60 * 60 * 1000 // 24小时检查一次
    this.init()
  }

  // 初始化回收站
  init() {
    // 启动自动清理
    this.startAutoCleanup()
    
    // 检查是否有回收站数据
    this.checkRecycleBinStatus()
  }

  // 检查回收站状态
  checkRecycleBinStatus() {
    const recycleBin = this.getRecycleBin()
    console.log(`🗑️ 回收站状态: ${recycleBin.items.length} 个项目`)
  }

  // 获取回收站数据
  getRecycleBin() {
    try {
      const data = localStorage.getItem(this.recycleBinKey)
      return data ? JSON.parse(data) : { items: [], lastCleanup: null }
    } catch (error) {
      console.error('获取回收站数据失败:', error)
      return { items: [], lastCleanup: null }
    }
  }

  // 保存回收站数据
  saveRecycleBin(recycleBin) {
    try {
      localStorage.setItem(this.recycleBinKey, JSON.stringify(recycleBin))
      return true
    } catch (error) {
      console.error('保存回收站数据失败:', error)
      return false
    }
  }

  // 软删除故事
  softDeleteStory(storyId, storyData, reason = '用户删除') {
    try {
      const recycleBin = this.getRecycleBin()
      
      const deletedItem = {
        id: `story-${storyId}-${Date.now()}`,
        originalId: storyId,
        type: 'story',
        data: storyData,
        deletedAt: new Date().toISOString(),
        deletedBy: 'user',
        reason: reason,
        size: new Blob([JSON.stringify(storyData)]).size
      }
      
      recycleBin.items.push(deletedItem)
      this.saveRecycleBin(recycleBin)
      
      console.log(`✅ 故事已移至回收站: ${storyData.title}`)
      return true
    } catch (error) {
      console.error('软删除故事失败:', error)
      return false
    }
  }

  // 软删除媒资
  softDeleteMedia(mediaId, mediaData, reason = '用户删除') {
    try {
      const recycleBin = this.getRecycleBin()
      
      const deletedItem = {
        id: `media-${mediaId}-${Date.now()}`,
        originalId: mediaId,
        type: 'media',
        data: mediaData,
        deletedAt: new Date().toISOString(),
        deletedBy: 'user',
        reason: reason,
        size: mediaData.size || 0
      }
      
      recycleBin.items.push(deletedItem)
      this.saveRecycleBin(recycleBin)
      
      console.log(`✅ 媒资已移至回收站: ${mediaData.name}`)
      return true
    } catch (error) {
      console.error('软删除媒资失败:', error)
      return false
    }
  }

  // 恢复项目
  restoreItem(itemId) {
    try {
      const recycleBin = this.getRecycleBin()
      const itemIndex = recycleBin.items.findIndex(item => item.id === itemId)
      
      if (itemIndex === -1) {
        throw new Error('回收站项目不存在')
      }
      
      const item = recycleBin.items[itemIndex]
      
      // 从回收站移除
      recycleBin.items.splice(itemIndex, 1)
      this.saveRecycleBin(recycleBin)
      
      // 恢复数据到原位置
      if (item.type === 'story') {
        this.restoreStory(item)
      } else if (item.type === 'media') {
        this.restoreMedia(item)
      }
      
      console.log(`✅ 项目已恢复: ${item.data.title || item.data.name}`)
      return { success: true, item }
    } catch (error) {
      console.error('恢复项目失败:', error)
      return { success: false, error: error.message }
    }
  }

  // 恢复故事
  restoreStory(item) {
    try {
      // 恢复到故事存储
      const storyKey = `altverse-story-${item.originalId}`
      localStorage.setItem(storyKey, JSON.stringify(item.data))
      
      // 更新故事列表
      const storiesKey = 'altverse-story-store'
      const storiesData = localStorage.getItem(storiesKey)
      if (storiesData) {
        const stories = JSON.parse(storiesData)
        if (stories.state && stories.state.stories) {
          // 检查是否已存在
          const existingIndex = stories.state.stories.findIndex(s => s.id === item.originalId)
          if (existingIndex === -1) {
            stories.state.stories.push(item.data)
            localStorage.setItem(storiesKey, JSON.stringify(stories))
          }
        }
      }
      
      console.log(`✅ 故事已恢复: ${item.data.title}`)
    } catch (error) {
      console.error('恢复故事失败:', error)
      throw error
    }
  }

  // 恢复媒资
  restoreMedia(item) {
    try {
      // 恢复到媒资存储
      const mediaKey = `altverse-media-${item.originalId}`
      localStorage.setItem(mediaKey, JSON.stringify(item.data))
      
      console.log(`✅ 媒资已恢复: ${item.data.name}`)
    } catch (error) {
      console.error('恢复媒资失败:', error)
      throw error
    }
  }

  // 永久删除项目
  permanentlyDeleteItem(itemId) {
    try {
      const recycleBin = this.getRecycleBin()
      const itemIndex = recycleBin.items.findIndex(item => item.id === itemId)
      
      if (itemIndex === -1) {
        throw new Error('回收站项目不存在')
      }
      
      const item = recycleBin.items[itemIndex]
      recycleBin.items.splice(itemIndex, 1)
      this.saveRecycleBin(recycleBin)
      
      console.log(`🗑️ 项目已永久删除: ${item.data.title || item.data.name}`)
      return { success: true, item }
    } catch (error) {
      console.error('永久删除项目失败:', error)
      return { success: false, error: error.message }
    }
  }

  // 清空回收站
  emptyRecycleBin() {
    try {
      const recycleBin = { items: [], lastCleanup: new Date().toISOString() }
      this.saveRecycleBin(recycleBin)
      
      console.log('🗑️ 回收站已清空')
      return { success: true, count: 0 }
    } catch (error) {
      console.error('清空回收站失败:', error)
      return { success: false, error: error.message }
    }
  }

  // 获取回收站统计
  getRecycleBinStats() {
    const recycleBin = this.getRecycleBin()
    const now = new Date()
    
    const stats = {
      totalItems: recycleBin.items.length,
      totalSize: recycleBin.items.reduce((sum, item) => sum + item.size, 0),
      totalSizeKB: 0,
      byType: {},
      byAge: {
        today: 0,
        week: 0,
        month: 0,
        older: 0
      },
      oldestItem: null,
      newestItem: null
    }
    
    stats.totalSizeKB = (stats.totalSize / 1024).toFixed(2)
    
    // 按类型统计
    recycleBin.items.forEach(item => {
      stats.byType[item.type] = (stats.byType[item.type] || 0) + 1
    })
    
    // 按年龄统计
    recycleBin.items.forEach(item => {
      const deletedAt = new Date(item.deletedAt)
      const daysDiff = (now - deletedAt) / (1000 * 60 * 60 * 24)
      
      if (daysDiff < 1) {
        stats.byAge.today++
      } else if (daysDiff < 7) {
        stats.byAge.week++
      } else if (daysDiff < 30) {
        stats.byAge.month++
      } else {
        stats.byAge.older++
      }
    })
    
    // 找到最旧和最新的项目
    if (recycleBin.items.length > 0) {
      const sortedItems = recycleBin.items.sort((a, b) => 
        new Date(a.deletedAt) - new Date(b.deletedAt)
      )
      stats.oldestItem = sortedItems[0]
      stats.newestItem = sortedItems[sortedItems.length - 1]
    }
    
    return stats
  }

  // 自动清理过期项目
  autoCleanup() {
    try {
      const recycleBin = this.getRecycleBin()
      const now = new Date()
      const cutoffDate = new Date(now.getTime() - (this.maxRetentionDays * 24 * 60 * 60 * 1000))
      
      const originalCount = recycleBin.items.length
      recycleBin.items = recycleBin.items.filter(item => {
        const deletedAt = new Date(item.deletedAt)
        return deletedAt > cutoffDate
      })
      
      const cleanedCount = originalCount - recycleBin.items.length
      recycleBin.lastCleanup = now.toISOString()
      
      this.saveRecycleBin(recycleBin)
      
      if (cleanedCount > 0) {
        console.log(`🧹 自动清理完成: 删除了 ${cleanedCount} 个过期项目`)
      }
      
      return { cleanedCount, remainingCount: recycleBin.items.length }
    } catch (error) {
      console.error('自动清理失败:', error)
      return { cleanedCount: 0, remainingCount: 0, error: error.message }
    }
  }

  // 启动自动清理
  startAutoCleanup() {
    // 立即执行一次清理
    this.autoCleanup()
    
    // 设置定时清理
    setInterval(() => {
      this.autoCleanup()
    }, this.autoCleanupInterval)
  }

  // 搜索回收站项目
  searchItems(query, filters = {}) {
    const recycleBin = this.getRecycleBin()
    let results = recycleBin.items
    
    // 按类型过滤
    if (filters.type) {
      results = results.filter(item => item.type === filters.type)
    }
    
    // 按时间范围过滤
    if (filters.dateRange) {
      const now = new Date()
      const days = filters.dateRange
      const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000))
      results = results.filter(item => new Date(item.deletedAt) >= cutoffDate)
    }
    
    // 按关键词搜索
    if (query) {
      const searchTerm = query.toLowerCase()
      results = results.filter(item => {
        const title = (item.data.title || item.data.name || '').toLowerCase()
        const reason = (item.reason || '').toLowerCase()
        return title.includes(searchTerm) || reason.includes(searchTerm)
      })
    }
    
    // 排序
    results.sort((a, b) => new Date(b.deletedAt) - new Date(a.deletedAt))
    
    return results
  }

  // 批量操作
  batchOperation(operation, itemIds) {
    const results = []
    
    itemIds.forEach(itemId => {
      let result
      switch (operation) {
        case 'restore':
          result = this.restoreItem(itemId)
          break
        case 'permanentlyDelete':
          result = this.permanentlyDeleteItem(itemId)
          break
        default:
          result = { success: false, error: '未知操作' }
      }
      results.push({ itemId, ...result })
    })
    
    return results
  }
}

// 创建单例实例
const recycleBinService = new RecycleBinService()

export default recycleBinService
